/* ProjectLP rifle configurator
 * Reads BOTH MK.6 and MK.6S data from the configurator root's data attributes.
 * Active model is set from the URL (?model=mk6 / ?model=mk6s) or the default.
 * Clicking a model card swaps everything client-side (title, price, gallery,
 * variants, swatches) and updates the URL via pushState — no page reload.
 *
 * State:
 *   activeModel  = 'mk6' | 'mk6s'
 *   finishTier   = Stain | Single | Two | CustomInquiry
 *   chassisColor = idx into CHASSIS_COLORS
 *   side, grip, standingBlock
 *
 * Variant matching is by option NAME (not position) since mk6 and mk6s order
 * their options differently.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-configurator]');
  if (!root) return;

  // ── Translated UI strings ───────────────────────────────────
  // Rendered by sections/rifle-builder.liquid from the locale files
  // (data-i18n). Display only: order properties, variant matching and the
  // inquiry email keep using the canonical English values below.
  let I18N = {};
  try { I18N = JSON.parse(root.dataset.i18n || '{}'); } catch (e) {}
  function t(key, fallback) {
    const v = I18N[key];
    return typeof v === 'string' && v ? v : fallback;
  }
  function colourLabel(c) {
    const map = I18N.colours || {};
    return map[c.n] || c.n;
  }

  // ── Display palette ─────────────────────────────────────────
  // Hexes sampled from the workshop paint cans (May 2026 photos).
  // variantName maps to a real Shopify variant Color value where one
  // exists; others fall back to "Custom Solid Colour" pricing.
  const CHASSIS_COLORS = [
    { n: 'Stain',    hex: '#7a5a3a', tier: 'Stain',   variantName: 'Stain' },
    { n: 'Red',          hex: '#b81e1e', tier: 'palette', variantName: 'Red' },
    { n: 'Dark Purple',  hex: '#3a1f4a', tier: 'palette' },
    { n: 'Desert Tan',   hex: '#c4a560', tier: 'palette', variantName: 'Desert Tan' },
    { n: 'Forest Green', hex: '#1a5d2e', tier: 'palette' },
    { n: 'Teal',     hex: '#44b8d0', tier: 'palette' },
    { n: 'Blue',     hex: '#16265a', tier: 'palette', variantName: 'Blue' },
    { n: 'Lavender', hex: '#b48ed4', tier: 'palette' },
    { n: 'Black',    hex: '#0e0e0e', tier: 'palette' },
    { n: 'Grey',     hex: '#8a8d91', tier: 'palette', variantName: 'Gray' },
    { n: 'White',    hex: '#f2f2ee', tier: 'palette' },
  ];

  // ── Load product data ───────────────────────────────────────
  function loadProduct(key) {
    if (!root.hasAttribute('data-' + key)) return null;
    let variants = [];
    let options = [];
    let media = [];
    let tierPrices = { stain: 0, single: 0, two: 0 };
    try { variants = JSON.parse(root.dataset[key + 'Variants'] || '[]'); } catch (e) {}
    try { options  = JSON.parse(root.dataset[key + 'Options']  || '[]'); } catch (e) {}
    try { media    = JSON.parse(root.dataset[key + 'Media']    || '[]'); } catch (e) {}
    // Tier prices are server-rendered (cents) so they reflect the logged-in
    // customer's B2B catalog price, not the public retail price.
    try {
      const tp = JSON.parse(root.dataset[key + 'TierPrices'] || '{}');
      tierPrices = {
        stain:  parseInt(tp.stain  || 0, 10),
        single: parseInt(tp.single || 0, 10),
        two:    parseInt(tp.two    || 0, 10),
      };
    } catch (e) {}
    // Normalize: each entry should have { src, alt, id }
    const images = media.map(m => {
      if (typeof m === 'string') return { src: m, alt: '', id: null };
      return { src: m.src || '', alt: m.alt || '', id: m.id || null };
    });
    return {
      handle:   root.dataset[key + 'Handle'] || '',
      title:    root.dataset[key + 'Title']  || '',
      priceMin: parseInt(root.dataset[key + 'Price'] || '0', 10),
      url:      root.dataset[key + 'Url']    || '',
      subtitle: root.dataset[key + 'Subtitle'] || '',
      variants, options, images, tierPrices,
    };
  }
  const products = { mk6: loadProduct('mk6'), mk6s: loadProduct('mk6s') };

  // ── Active model from URL or default ────────────────────────
  function readModelFromURL() {
    const params = new URLSearchParams(window.location.search);
    const m = params.get('model');
    if (m === 'mk6' && products.mk6) return 'mk6';
    if (m === 'mk6s' && products.mk6s) return 'mk6s';
    const def = root.dataset.defaultModel || 'mk6s';
    if (products[def]) return def;
    return products.mk6s ? 'mk6s' : 'mk6';
  }

  const state = {
    activeModel: readModelFromURL(),
    finishTier: 'Stain',
    chassisColor: 0,
    chassisColor2: 1,
    side: 'Right',
    grip: 'Wide',
    standingBlock: 'ProjectLP Original',
    cheekpiece: 'Regular',
    // galleryIdx: index into the items[] array built in renderGallery().
    // 0 = stain thumb (when Stain mode is active), then product images.
    galleryIdx: 0,
    showTintPreview: true,
  };

  // ── DOM refs ────────────────────────────────────────────────
  const currentTitle = document.querySelector('[data-current-title]');
  const configTitle = root.querySelector('[data-config-title]');
  const configFromPrice = root.querySelector('[data-config-from-price]');
  const configSubtitle = root.querySelector('[data-config-subtitle]');
  const galleryMain = document.querySelector('[data-gallery-main]');
  const galleryBracket = document.querySelector('[data-gallery-bracket]');
  const galleryThumbs = document.querySelector('[data-gallery-thumbs]');
  const tintCanvas = document.querySelector('[data-tint-canvas]');
  const tintLabel = document.querySelector('[data-tint-label]');
  const swatchGrid = root.querySelector('[data-step="chassisColor"]');
  const variantInput = root.querySelector('[data-input-variant]');
  const summaryPrice = root.querySelector('[data-summary-price]');
  const summaryLabel = root.querySelector('[data-summary-label]');
  const submitBtn = root.querySelector('[data-submit-btn]');
  const msg = root.querySelector('[data-config-message]');
  const referenceUrl = root.dataset.referenceImage || '';
  const stainImageUrl = root.dataset.stainImage || '';

  // ── Money formatting ────────────────────────────────────────
  function formatMoney(cents) {
    const v = (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
    return '$' + v + ' CAD';
  }

  // ── Render gallery from active product images ───────────────
  // ── Gallery rendering ───────────────────────────────────────────
  // Gallery items are built fresh each call so tier switches and model
  // switches always reflect the current state without async coupling.
  //
  // When Stain finish is selected the dedicated stain photo is injected
  // as item[0] (always active on first load / tier switch).  Product
  // photos follow it.  For every other tier only product photos appear.
  //
  // state.galleryIdx  — index into the items[] array built here.
  //   0              — stain photo (when in Stain mode)
  //   0 / 1 / 2 …    — product photos
  //
  // This replaces the old stainShown flag / updateTintRender override
  // approach, which was brittle because it depended on the async
  // reference-image load having already fired before the first click.
  function renderGallery() {
    const p = products[state.activeModel];
    if (!p || !galleryMain || !galleryThumbs) return;
    galleryThumbs.innerHTML = '';

    const isStainMode = state.finishTier === 'Stain' && stainImageUrl;

    // Build ordered list of gallery items
    const items = [];
    if (isStainMode) {
      items.push({ src: stainImageUrl, alt: t('stain_finish', 'Stain finish'), isStain: true });
    }
    p.images.forEach(img => items.push({ src: img.src, alt: img.alt || p.title, id: img.id }));

    if (items.length === 0) {
      galleryMain.removeAttribute('src');
      galleryMain.alt = p.title;
      return;
    }

    // Clamp active index to valid range
    const idx = Math.min(Math.max(0, state.galleryIdx || 0), items.length - 1);
    state.galleryIdx = idx;

    // Show selected item in main slot
    galleryMain.src = buildImageUrl(items[idx].src, 1600);
    galleryMain.alt = items[idx].alt;
    galleryMain.style.visibility = '';
    if (galleryBracket) galleryBracket.textContent = 'FIG. 01 — ' + p.title.toUpperCase();

    // Build thumb strip — only when there are multiple items to navigate between
    if (items.length > 1) {
      items.forEach((item, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-thumb-img-btn' + (i === idx ? ' is-active' : '');
        btn.innerHTML = '<img src="' + buildImageUrl(item.src, 320) + '" alt="" loading="lazy">';
        btn.addEventListener('click', function () {
          state.galleryIdx = i;
          state.showTintPreview = item.isStain ? true : false;
          galleryMain.src = buildImageUrl(item.src, 1600);
          galleryMain.alt = item.alt;
          galleryMain.style.visibility = '';
          galleryThumbs.querySelectorAll('.pdp-thumb-img-btn').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
          // For product-photo thumbs in Single Colour mode, hide the tint
          // canvas so the user can inspect the real product photo.
          if (!item.isStain) updateTintRender();
        });
        galleryThumbs.appendChild(btn);
      });
    }
  }

  // Add a Shopify image width param so we don't fetch the original.
  // Shopify CDN URLs often come in as protocol-relative (//cdn.shopify.com/...).
  function buildImageUrl(src, width) {
    if (!src) return '';
    let normalized = src;
    if (src.indexOf('//') === 0) normalized = window.location.protocol + src;
    try {
      const u = new URL(normalized, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) { return src; }
  }

  // ── Render head (title, price, subtitle, current selector text) ──
  function renderHead() {
    const p = products[state.activeModel];
    if (!p) return;
    if (currentTitle) currentTitle.textContent = p.title.toUpperCase();
    if (configTitle) configTitle.textContent = p.title;
    if (configFromPrice) configFromPrice.textContent = formatMoney(p.priceMin);
    if (configSubtitle) configSubtitle.textContent = p.subtitle;
    renderTierPrices();
  }

  // ── Refresh per-tier prices in the finish-tier radio labels ──
  // Liquid renders the active model's prices on first load; this rewrites
  // them when the user swaps models so the label always tracks the
  // catalog price for the currently selected product.
  // Prefix per tier:
  //   stain  → "$X CAD · "
  //   single → "From $X CAD · "  (min across solid colours)
  //   two    → "$X CAD · "
  function renderTierPrices() {
    const p = products[state.activeModel];
    if (!p) return;
    const prefixFor = {
      stain:  c => c > 0 ? formatMoney(c) + ' · ' : '',
      single: c => c > 0 ? t('from_price', 'From __PRICE__').replace('__PRICE__', formatMoney(c)) + ' · ' : '',
      two:    c => c > 0 ? formatMoney(c) + ' · ' : '',
    };
    Object.keys(prefixFor).forEach(tier => {
      const el = root.querySelector('[data-tier-price="' + tier + '"]');
      if (el) el.textContent = prefixFor[tier](p.tierPrices[tier] || 0);
    });
  }

  // ── Render model cards active state ─────────────────────────
  function renderModelCards() {
    document.querySelectorAll('[data-model-pick]').forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.modelPick === state.activeModel);
    });
  }

  // ── Swatches ────────────────────────────────────────────────
  function renderSwatches() {
    if (!swatchGrid) return;
    swatchGrid.innerHTML = '';
    CHASSIS_COLORS.forEach((c, i) => {
      if (state.finishTier === 'Stain' && c.tier !== 'Stain') return;
      if (state.finishTier !== 'Stain' && c.tier === 'Stain') return;
      const isPrimary = i === state.chassisColor;
      const isSecondary = state.finishTier === 'Two' && i === state.chassisColor2;
      const cls = 'swatch' + (isPrimary ? ' is-primary' : '') + (isSecondary ? ' is-secondary' : '');
      const el = document.createElement('div');
      el.className = cls;
      el.innerHTML = `
        <div class="chip" style="background:${c.hex};">
          ${isPrimary ? '<span class="chip-label">1</span>' : ''}
          ${isSecondary ? '<span class="chip-label">2</span>' : ''}
        </div>
        <div class="lbl">${colourLabel(c)}</div>
      `;
      el.addEventListener('click', () => onSwatchClick(i));
      swatchGrid.appendChild(el);
    });
    const lbl = root.querySelector('[data-step-label="chassisColor"]');
    if (lbl) lbl.textContent = state.finishTier === 'Two'
      ? t('chassis_colours_two', '02 · CHASSIS COLOURS (2)')
      : t('chassis_colour', '02 · CHASSIS COLOUR');
    emitConfig();
  }

  // ── Broadcast the build so the 3D viewers (mk61-viewer.js) follow it ──
  // Fires on every swatch render (init, tier change, colour pick) and on every
  // other option change. The last value is also kept on window so a viewer that
  // finishes loading later can catch up.
  function emitConfig() {
    const detail = {
      tier: state.finishTier,
      primary: CHASSIS_COLORS[state.chassisColor].hex,
      secondary: state.finishTier === 'Two' ? CHASSIS_COLORS[state.chassisColor2].hex : null,
      side: state.side,
      grip: state.grip,
      standingBlock: state.standingBlock,
      cheekpiece: state.cheekpiece,
    };
    window.projectlpConfig = detail;
    document.dispatchEvent(new CustomEvent('projectlp:config', { detail: detail }));
  }

  function onSwatchClick(i) {
    if (state.finishTier === 'Two') {
      if (i === state.chassisColor) return;
      if (i === state.chassisColor2) {
        const tmp = state.chassisColor;
        state.chassisColor = i;
        state.chassisColor2 = tmp;
      } else {
        state.chassisColor2 = state.chassisColor;
        state.chassisColor = i;
      }
    } else {
      state.chassisColor = i;
    }
    // Picking a colour means the user wants to see the tint preview again.
    state.showTintPreview = true;
    renderSwatches();
    updateSummary();
    updateTintRender();
  }

  // ── Live-tint render (Single Colour only) ───────────────────
  let refImg = null;
  let refCrop = null;
  function loadReference() {
    if (!referenceUrl || !tintCanvas) return;
    // Use fetch+blob to load the reference image so that drawing it onto a
    // canvas and calling getImageData() is always allowed.  Loading via a
    // plain <img crossOrigin="anonymous"> can silently fail when the browser
    // has a cached copy of the asset that was served without CORS headers,
    // tainting the canvas and leaving refImg null.
    var url = referenceUrl;
    // Resolve protocol-relative URLs the same way buildImageUrl does.
    if (url.indexOf('//') === 0) url = window.location.protocol + url;
    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.blob();
      })
      .then(function (blob) {
        var blobUrl = URL.createObjectURL(blob);
        var img = new Image();
        img.onload = function () {
          // Tight crop around non-near-black pixels.
          var off = document.createElement('canvas');
          off.width = img.naturalWidth; off.height = img.naturalHeight;
          var octx = off.getContext('2d');
          octx.drawImage(img, 0, 0);
          var d;
          try { d = octx.getImageData(0, 0, off.width, off.height).data; }
          catch (e) { return; }
          var minX = off.width, minY = off.height, maxX = 0, maxY = 0;
          var T = 38;
          for (var y = 0; y < off.height; y += 2) {
            for (var x = 0; x < off.width; x += 2) {
              var pi = (y * off.width + x) * 4;
              var lum = (d[pi] + d[pi + 1] + d[pi + 2]) / 3;
              if (lum > T) {
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
              }
            }
          }
          var pad = 8;
          minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
          maxX = Math.min(off.width - 1, maxX + pad); maxY = Math.min(off.height - 1, maxY + pad);
          // Bail out if no bright pixels were found (bad/missing reference asset).
          if (maxX <= minX || maxY <= minY) return;
          refImg = img;
          refCrop = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
          updateTintRender();
        };
        img.src = blobUrl;
      })
      .catch(function () {
        // Reference image unavailable — tint preview is silently disabled.
      });
  }

  function updateTintRender() {
    if (!tintCanvas || !galleryMain) return;
    // Stain tier: the gallery is fully managed by renderGallery() — no
    // override here.  Just ensure the canvas stays hidden.
    if (state.finishTier === 'Stain') {
      tintCanvas.hidden = true;
      if (tintLabel) tintLabel.hidden = true;
      galleryMain.style.visibility = '';
      return;
    }
    const active = state.finishTier === 'Single' && state.showTintPreview;
    if (!active || !refImg) {
      tintCanvas.hidden = true;
      if (tintLabel) tintLabel.hidden = true;
      galleryMain.style.visibility = '';
      return;
    }
    // Tint to selected colour
    if (!refCrop || refCrop.w <= 0 || refCrop.h <= 0) {
      tintCanvas.hidden = true;
      if (tintLabel) tintLabel.hidden = true;
      galleryMain.style.visibility = '';
      return;
    }
    const crop = refCrop;
    tintCanvas.width = crop.w; tintCanvas.height = crop.h;
    const ctx = tintCanvas.getContext('2d');
    ctx.drawImage(refImg, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
    let data;
    try { data = ctx.getImageData(0, 0, crop.w, crop.h); } catch (e) { return; }
    const px = data.data;
    const tintHex = CHASSIS_COLORS[state.chassisColor].hex;
    const back = hexToRgb(tintHex);
    const backHSL = rgbToHsl(back.r, back.g, back.b);
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i], g = px[i+1], b = px[i+2];
      const max = Math.max(r, g, b);
      const isChassis = r > 90 && (r - g) > 22 && (r - b) > 22 && max > 80;
      if (!isChassis) continue;
      const v = max / 255;
      const stretched = Math.max(0, Math.min(1, (v - 0.5) * 1.7 + 0.5));
      const lo = Math.max(0.04, backHSL.l - 0.18);
      const hi = Math.min(0.94, backHSL.l + 0.20);
      const outL = lo + (hi - lo) * stretched;
      const isAch = backHSL.s < 0.08;
      const outS = isAch ? 0 : Math.min(1, Math.max(0.85, backHSL.s * 1.6));
      const out = hslToRgb(backHSL.h, outS, outL);
      px[i] = out.r; px[i+1] = out.g; px[i+2] = out.b;
    }
    ctx.putImageData(data, 0, 0);
    tintCanvas.hidden = false;
    if (tintLabel) tintLabel.hidden = false;
    galleryMain.style.visibility = 'hidden';
  }

  function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  }
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = 0; s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return { h, s, l };
  }
  function hslToRgb(h, s, l) {
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
      const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  }

  // ── Variant matching (by option NAME) ───────────────────────
  function findOptionIndex(product, predicate) {
    for (let i = 0; i < product.options.length; i++) {
      const name = (product.options[i].name || '').toLowerCase();
      if (predicate(name)) return i;
    }
    return -1;
  }

  function targetColorValue() {
    if (state.finishTier === 'Stain') return 'Stain';
    if (state.finishTier === 'Two')   return 'Custom Multi Colour';
    if (state.finishTier === 'CustomInquiry') return null;
    const c = CHASSIS_COLORS[state.chassisColor];
    return c.variantName || 'Custom Solid Colour';
  }

  function findVariant() {
    const color = targetColorValue();
    if (!color) return null;
    const p = products[state.activeModel];
    if (!p) return null;
    // A palette colour with its own variant on one model (e.g. Gray on the Mk.6S)
    // may not exist on the other — price it as a custom solid colour there.
    return findVariantFor(p, color) ||
      (state.finishTier === 'Single' && color !== 'Custom Solid Colour' ? findVariantFor(p, 'Custom Solid Colour') : undefined);
  }

  function findVariantFor(p, color) {
    const colorIdx = findOptionIndex(p, n => n === 'color' || n.includes('colour'));
    const sideIdx  = findOptionIndex(p, n => n.includes('left') || n.includes('right') || n.includes('side') || n.includes('hand'));
    const colorLc = color.toLowerCase();
    const sideLc  = state.side.toLowerCase();
    return p.variants.find(v => {
      const opts = [v.option1, v.option2, v.option3].map(x => (x || '').toString().toLowerCase());
      const colorOk = colorIdx === -1 ? true : opts[colorIdx] === colorLc;
      const sideOk  = sideIdx  === -1 ? true : opts[sideIdx]  === sideLc;
      return colorOk && sideOk;
    });
  }

  function updateSummary() {
    const needsInquiry =
      state.finishTier === 'CustomInquiry' ||
      state.grip === 'Custom' ||
      state.standingBlock === 'Custom';

    if (needsInquiry) {
      summaryLabel.textContent =
        state.finishTier === 'CustomInquiry' ? t('quote_required', 'Quote required') : t('inquiry_required', 'Inquiry required');
      summaryPrice.textContent = t('by_quote', 'BY QUOTE');
      submitBtn.textContent = t('submit_inquiry', 'Submit inquiry →');
      submitBtn.setAttribute('data-mode', 'inquiry');
      submitBtn.classList.remove('is-disabled');
      if (variantInput) variantInput.value = '';
      return;
    }
    const v = findVariant();
    if (!v) {
      summaryLabel.textContent = t('combination_unavailable', 'Combination unavailable');
      summaryPrice.textContent = '—';
      submitBtn.classList.add('is-disabled');
      submitBtn.setAttribute('data-mode', 'disabled');
      if (variantInput) variantInput.value = '';
      return;
    }
    submitBtn.classList.remove('is-disabled');
    summaryLabel.textContent = t('configured_total', 'Configured total');
    summaryPrice.textContent = formatMoney(v.price);
    submitBtn.textContent = t('add_to_cart', 'Add to cart →');
    submitBtn.setAttribute('data-mode', 'cart');
    if (variantInput) variantInput.value = v.id;
    // Intentionally do NOT auto-swap the gallery to the variant's
    // Shopify-assigned image. In this configurator the gallery is owned
    // by tier state (Stain photo / tint canvas) and the user's thumb
    // picks — auto-syncing here would clobber the Stain photo when
    // returning to Stain after Single/Two, and would yank the gallery
    // away from a photo the user explicitly clicked.
  }

  function showVariantImage(imageId) {
    const p = products[state.activeModel];
    if (!p) return;
    const productIdx = p.images.findIndex(img => img.id === imageId);
    if (productIdx === -1) return;
    // In Stain mode item[0] is the stain thumb, so product images are offset by 1.
    const itemIdx = (state.finishTier === 'Stain' && stainImageUrl) ? productIdx + 1 : productIdx;
    if (itemIdx === state.galleryIdx) return;
    state.galleryIdx = itemIdx;
    renderGallery();
  }

  // ── Wire up radio rows ──────────────────────────────────────
  root.querySelectorAll('[data-step]').forEach(group => {
    const step = group.dataset.step;
    if (step === 'chassisColor') return;
    group.addEventListener('click', e => {
      const row = e.target.closest('.radio-row');
      if (!row) return;
      const value = row.dataset.value;
      group.querySelectorAll('.radio-row').forEach(r => r.classList.toggle('is-active', r === row));
      if (step === 'finishTier') {
        state.finishTier = value;
        root.setAttribute('data-tier', value);
        state.showTintPreview = true;
        // When switching tiers reset the gallery to the first slot
        // (stain thumb for Stain, first product photo otherwise).
        state.galleryIdx = 0;
        if (value === 'Stain') {
          state.chassisColor = 0; state.chassisColor2 = 0;
        } else if (value === 'Single' || value === 'Two') {
          if (CHASSIS_COLORS[state.chassisColor].tier === 'Stain') state.chassisColor = 1;
          if (state.chassisColor2 === state.chassisColor) state.chassisColor2 = state.chassisColor === 1 ? 2 : 1;
        }
        renderSwatches();
        renderGallery();
        updateTintRender();
      } else {
        state[step] = value;
        emitConfig();
      }
      updateSummary();
    });
  });

  // ── Model card click → swap active model ────────────────────
  function setModel(model, pushUrl) {
    if (!products[model]) return;
    state.activeModel = model;
    state.galleryIdx = 0;
    // Fresh model → start in tint preview again so the user sees
    // their colour choice on the new chassis.
    state.showTintPreview = true;
    renderModelCards();
    renderHead();
    renderGallery();
    updateSummary();
    updateTintRender();
    if (pushUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('model', model);
      window.history.pushState({}, '', url.toString());
    }
  }

  document.querySelectorAll('[data-model-pick]').forEach(btn => {
    btn.addEventListener('click', () => setModel(btn.dataset.modelPick, true));
  });

  // Back/forward navigation
  window.addEventListener('popstate', () => {
    const m = readModelFromURL();
    setModel(m, false);
  });

  // ── Form submit ─────────────────────────────────────────────
  const form = root.querySelector('[data-config-form]');
  form && form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const mode = submitBtn.getAttribute('data-mode');
    if (mode === 'disabled') return;
    if (mode === 'inquiry') {
      const inquiryEmail = root.dataset.inquiryEmail || '';
      const subj = encodeURIComponent('Mk.6 custom build inquiry');
      const p = products[state.activeModel];
      const lines = [
        'Model: ' + (p ? p.title : state.activeModel),
        'Finish tier: ' + state.finishTier,
        state.finishTier === 'CustomInquiry' ? 'Finish: custom (details to follow)' : 'Chassis colour: ' + CHASSIS_COLORS[state.chassisColor].n,
        state.finishTier === 'Two' ? 'Chassis colour 2: ' + CHASSIS_COLORS[state.chassisColor2].n : null,
        'Side: ' + state.side,
        'Pistol grip: ' + state.grip,
        'Standing block: ' + state.standingBlock,
        'Cheekpiece height: ' + state.cheekpiece,
      ].filter(Boolean);
      const body = encodeURIComponent(lines.join('\n'));
      window.location.href = 'mailto:' + inquiryEmail + '?subject=' + subj + '&body=' + body;
      return;
    }
    const v = findVariant();
    if (!v) return;
    const props = {
      'Chassis colour': CHASSIS_COLORS[state.chassisColor].n,
    };
    if (state.finishTier === 'Two') props['Chassis colour (front)'] = CHASSIS_COLORS[state.chassisColor2].n;
    props['Pistol grip'] = state.grip;
    props['Standing block'] = state.standingBlock;
    props['Cheekpiece height'] = state.cheekpiece;
    submitBtn.disabled = true;
    submitBtn.textContent = t('adding', 'Adding…');
    try {
      const res = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: props }] }),
      });
      if (!res.ok) throw new Error('Cart add failed');
      window.location.href = window.theme.routes.cart;
    } catch (err) {
      submitBtn.disabled = false;
      submitBtn.textContent = t('add_to_cart', 'Add to cart →');
      if (msg) {
        msg.style.color = '#ff8888';
        msg.textContent = t('add_error', 'Could not add to cart. Please try again.');
      }
    }
  });

  // ── Init ────────────────────────────────────────────────────
  root.setAttribute('data-tier', state.finishTier);
  renderModelCards();
  renderHead();
  renderGallery();
  renderSwatches();
  updateSummary();
  loadReference();
})();
